﻿/**
 * EventList - 浜嬩欢鍒楄〃椤甸潰
 */

import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDates, useEvents, useNarratives } from '../hooks/useEvents'
import { useTokenPrices } from '../hooks/useTokenPrices'
import { useLanguage } from '../hooks/useLanguage'
import { Header } from '../components/Header'
import { CategoryTabs } from '../components/CategoryTabs'
import { EventCard } from '../components/EventCard'
import { TokenSearch } from '../components/TokenSearch'
import { AppBottomNav } from '../components/AppBottomNav'
import { LoadingState, ErrorState, EmptyState } from '../components/StateComponents'
import { getNarrativeDisplayName } from '../utils/category'
import { getTelegramUserId } from '../utils/telegram'
import { apiClient } from '../api/client'
import { requireCredit } from '../utils/billing'
import {
  clearAnalyticsPageContext,
  getActiveInteractionId,
  getAnalyticsContext,
  setAnalyticsPageContext,
  trackBusinessEvent,
} from '../utils/analytics'
import type { EventSummary } from '../types/event'

interface FollowItem {
  event_id: string
  event_instance_id: string
}

interface FollowsResponse {
  follows: FollowItem[]
  count: number
}

interface LikeItem {
  event_id: string
  event_instance_id: string
}

interface LikesResponse {
  likes: LikeItem[]
  count: number
}

interface FollowToggleResult {
  success: boolean
  active_count: number
  max_count: number
  error?: string
}

interface LikeToggleResult {
  success: boolean
  liked: boolean
}

interface NarrativeSession {
  date: string
  narrative: string
  startedAt: number
  initialLoadedCount: number
  maxLoadedCount: number
  totalEvents: number
}

const EVENT_SCROLL_KEY = 'events:scrollY'
const EVENT_DATE_KEY = 'events:selectedDate'
const EVENT_NARRATIVE_KEY = 'events:selectedNarrative'
const EVENT_PAGE_KEY = 'events:page'
const EVENT_TOKEN_KEY = 'events:selectedToken'
const PAGE_SIZE = 20

export function EventList() {
  const navigate = useNavigate()
  const { lang, t } = useLanguage()
  const { data: tokenPricesData } = useTokenPrices()

  // 鐘舵€?
  const [selectedDate, setSelectedDate] = useState<string | undefined>(() => {
    return sessionStorage.getItem(EVENT_DATE_KEY) || undefined
  })
  const [selectedNarrative, setSelectedNarrative] = useState<string | undefined>(() => {
    return sessionStorage.getItem(EVENT_NARRATIVE_KEY) || undefined
  })
  const [selectedToken, setSelectedToken] = useState<string | undefined>(() => {
    return sessionStorage.getItem(EVENT_TOKEN_KEY) || undefined
  })
  const [page, setPage] = useState(() => {
    const savedPage = Number(sessionStorage.getItem(EVENT_PAGE_KEY) || '1')
    return Number.isFinite(savedPage) && savedPage > 0 ? savedPage : 1
  })
  const [followedEventIds, setFollowedEventIds] = useState<Set<string>>(new Set())
  const [likedEventIds, setLikedEventIds] = useState<Set<string>>(new Set())
  const [followCount, setFollowCount] = useState(0)
  const [maxFollowCount, setMaxFollowCount] = useState(3)
  const [displayedEvents, setDisplayedEvents] = useState<EventSummary[]>([])
  const [followNotice, setFollowNotice] = useState<string | null>(null)
  const followCountRef = useRef(0)
  const maxFollowCountRef = useRef(3)
  const pendingFollowAddsRef = useRef(0)
  const followNoticeTimerRef = useRef<number | null>(null)
  const didMountResetRef = useRef(false)
  const restoredScrollRef = useRef(false)
  const narrativeSessionRef = useRef<NarrativeSession | null>(null)
  const totalEventsRef = useRef(0)

  // 鏁版嵁鑾峰彇
  const { data: datesData, isLoading: datesLoading } = useDates()
  const { data: narrativesData, isLoading: narrativesLoading } = useNarratives(selectedDate)
  const { data: eventsData, isLoading: eventsLoading, error, refetch } = useEvents({
    date: selectedDate,
    narrative: selectedNarrative,
    token: selectedToken,
    page,
    limit: PAGE_SIZE,
  })

  // 鑷姩閫夋嫨鏈€鏂版棩鏈?
  useEffect(() => {
    if (datesData?.latest && !selectedDate) {
      setSelectedDate(datesData.latest)
    }
  }, [datesData, selectedDate])

  // 閲嶇疆椤电爜
  useEffect(() => {
    if (!didMountResetRef.current) {
      didMountResetRef.current = true
      return
    }
    setPage(1)
    setDisplayedEvents([])
    sessionStorage.removeItem(EVENT_SCROLL_KEY)
    restoredScrollRef.current = true
    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [selectedDate, selectedNarrative, selectedToken, lang])

  useEffect(() => {
    if (!eventsData?.events) {
      return
    }

    if (page === 1) {
      setDisplayedEvents(eventsData.events)
      return
    }

    setDisplayedEvents((prev) => {
      const seen = new Set(prev.map((event) => event.event_instance_id))
      const nextPageEvents = eventsData.events.filter((event) => !seen.has(event.event_instance_id))
      return [...prev, ...nextPageEvents]
    })
  }, [eventsData, page])

  useEffect(() => {
    if (selectedDate) {
      sessionStorage.setItem(EVENT_DATE_KEY, selectedDate)
    }
  }, [selectedDate])

  useEffect(() => {
    sessionStorage.setItem(EVENT_NARRATIVE_KEY, selectedNarrative || '')
  }, [selectedNarrative])

  useEffect(() => {
    sessionStorage.setItem(EVENT_TOKEN_KEY, selectedToken || '')
  }, [selectedToken])

  useEffect(() => {
    sessionStorage.setItem(EVENT_PAGE_KEY, String(page))
  }, [page])

  useEffect(() => {
    if (eventsData?.total !== undefined) {
      totalEventsRef.current = eventsData.total
    }
  }, [eventsData?.total])

  const finishNarrativeSession = (
    exitReason: string,
    extra: Record<string, unknown> = {},
    useBeacon = false,
  ) => {
    const session = narrativeSessionRef.current
    if (!session) return
    narrativeSessionRef.current = null
    void trackBusinessEvent('narrative_exit', {
      narrative: session.narrative,
      date: session.date,
      duration_ms: Date.now() - session.startedAt,
      initial_loaded_count: session.initialLoadedCount,
      loaded_count: session.maxLoadedCount,
      total_events: session.totalEvents,
      exit_reason: exitReason,
      ...extra,
    }, undefined, undefined, useBeacon)
  }

  useEffect(() => {
    const totalEvents = eventsData?.total ?? totalEventsRef.current ?? displayedEvents.length
    const scrollSegment = Math.max(1, Math.ceil(Math.max(displayedEvents.length, 1) / PAGE_SIZE))
    const scrollScope = `${selectedDate || 'unknown'}:${selectedNarrative || 'all'}:${scrollSegment}`
    setAnalyticsPageContext({
      selected_date: selectedDate,
      selected_narrative: selectedNarrative || 'all',
      loaded_count: displayedEvents.length,
      total_events: totalEvents,
      current_page: page,
      scroll_segment: scrollSegment,
      scroll_scope: scrollScope,
    })
    return () => clearAnalyticsPageContext([
      'selected_date',
      'selected_narrative',
      'loaded_count',
      'total_events',
      'current_page',
      'scroll_segment',
      'scroll_scope',
    ])
  }, [selectedDate, selectedNarrative, displayedEvents.length, eventsData?.total, page])

  useEffect(() => {
    if (!selectedDate || eventsLoading || displayedEvents.length === 0) return
    const narrative = selectedNarrative || 'all'
    const startedAt = Date.now()
    const timer = window.setTimeout(() => {
      const contentHeight = document.documentElement.scrollHeight
      const viewportHeight = window.innerHeight
      const scrollRequired = contentHeight > viewportHeight + 1
      void trackBusinessEvent('narrative_view', {
        narrative,
        date: selectedDate,
        qualified_after_ms: Date.now() - startedAt,
        events_seen: displayedEvents.length,
        total_events: eventsData?.total || 0,
        scroll_required: scrollRequired,
        fully_visible: !scrollRequired,
        content_height: contentHeight,
        viewport_height: viewportHeight,
      })
    }, 2000)
    return () => window.clearTimeout(timer)
  }, [selectedDate, selectedNarrative, eventsLoading, displayedEvents.length, eventsData?.total])

  useEffect(() => {
    if (!selectedDate || eventsLoading || displayedEvents.length === 0) return
    const narrative = selectedNarrative || 'all'
    const existing = narrativeSessionRef.current

    if (!existing || existing.date !== selectedDate || existing.narrative !== narrative) {
      finishNarrativeSession('context_change', {
        next_date: selectedDate,
        next_narrative: narrative,
      })
      narrativeSessionRef.current = {
        date: selectedDate,
        narrative,
        startedAt: Date.now(),
        initialLoadedCount: displayedEvents.length,
        maxLoadedCount: displayedEvents.length,
        totalEvents: eventsData?.total || 0,
      }
      return
    }

    existing.maxLoadedCount = Math.max(existing.maxLoadedCount, displayedEvents.length)
    existing.totalEvents = eventsData?.total || existing.totalEvents
  }, [selectedDate, selectedNarrative, eventsLoading, displayedEvents.length, eventsData?.total])

  useEffect(() => {
    return () => finishNarrativeSession('route_change_or_unmount')
  }, [])

  useEffect(() => {
    const handlePageClose = () => finishNarrativeSession('page_close', {}, true)
    window.addEventListener('beforeunload', handlePageClose)
    window.addEventListener('pagehide', handlePageClose)
    return () => {
      window.removeEventListener('beforeunload', handlePageClose)
      window.removeEventListener('pagehide', handlePageClose)
    }
  }, [])

  useEffect(() => {
    if (restoredScrollRef.current || eventsLoading || displayedEvents.length === 0) return
    const savedScrollY = Number(sessionStorage.getItem(EVENT_SCROLL_KEY) || '0')
    if (!savedScrollY) return

    restoredScrollRef.current = true
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: savedScrollY, behavior: 'auto' })
    })
  }, [displayedEvents.length, eventsLoading])

  // 鍙欎簨鍒楄〃
  const narratives = narrativesData?.narratives || []
  const selectedNarrativeName = selectedNarrative
    ? getNarrativeDisplayName(selectedNarrative)
    : undefined

  const loadFollows = async () => {
    const data = await apiClient.get<FollowsResponse>('/api/user/follows', {
      chat_id: getTelegramUserId(),
      lang,
      date: selectedDate || '',
    })
    setFollowedEventIds(new Set((data.follows || []).map((item) => item.event_instance_id)))
    setFollowCount(data.count || 0)
  }

  const loadLikes = async () => {
    const data = await apiClient.get<LikesResponse>('/api/user/likes', {
      chat_id: getTelegramUserId(),
      date: selectedDate || '',
    })
    setLikedEventIds(new Set((data.likes || []).map((item) => item.event_instance_id)))
  }

  useEffect(() => {
    loadFollows().catch((err) => {
      console.error('Failed to load follows:', err)
    })
    loadLikes().catch((err) => {
      console.error('Failed to load likes:', err)
    })
  }, [lang, selectedDate])

  useEffect(() => {
    followCountRef.current = followCount
    maxFollowCountRef.current = maxFollowCount
  }, [followCount, maxFollowCount])

  useEffect(() => {
    return () => {
      if (followNoticeTimerRef.current !== null) {
        window.clearTimeout(followNoticeTimerRef.current)
      }
    }
  }, [])

  const showFollowNotice = (message: string) => {
    setFollowNotice(message)

    if (followNoticeTimerRef.current !== null) {
      window.clearTimeout(followNoticeTimerRef.current)
    }

    followNoticeTimerRef.current = window.setTimeout(() => {
      setFollowNotice(null)
      followNoticeTimerRef.current = null
    }, 1800)
  }

  const saveEventListPosition = () => {
    sessionStorage.setItem(EVENT_SCROLL_KEY, String(window.scrollY))
    sessionStorage.setItem(EVENT_DATE_KEY, selectedDate || '')
    sessionStorage.setItem(EVENT_NARRATIVE_KEY, selectedNarrative || '')
    sessionStorage.setItem(EVENT_PAGE_KEY, String(page))
  }

  // 澶勭悊浜嬩欢鐐瑰嚮
  const handleEventClick = async (event: EventSummary, index: number) => {
    trackBusinessEvent('event_detail_click', {
      event_id: event.event_id,
      event_title: event.title,
      narrative: selectedNarrative || event.narrative,
      index,
      total: eventsData?.total || 0,
      source_page: 'home',
    }, event.event_id)
    const ok = await requireCredit('view_detail', event.event_id, t('查看详情', 'View Details'), showFollowNotice)
    if (!ok) return
    saveEventListPosition()

    const totalInCategory = eventsData?.total || 0
    navigate(`/event/${event.event_id}?date=${selectedDate || 'latest'}&narrative=${selectedNarrative || ''}&index=${index}&total=${totalInCategory}`)
  }

  // 澶勭悊鏃ユ湡鍒囨崲
  const handleDateChange = (date: string) => {
    if (date === selectedDate) return
    const interactionId = getActiveInteractionId()
    finishNarrativeSession('date_change', {
      next_date: date,
      next_narrative: 'all',
    })
    void trackBusinessEvent('date_change', {
      from_date: selectedDate,
      to_date: date,
      from_narrative: selectedNarrative || 'all',
      to_narrative: 'all',
      source_page: 'home',
      reason: 'user_select_date',
      reset_page_to: 1,
    }, undefined, interactionId)
    setSelectedDate(date)
    setSelectedNarrative(undefined)

  // Handle narrative switch
  }

  const handleNarrativeChange = (narrative?: string) => {
    finishNarrativeSession('narrative_change', {
      next_date: selectedDate,
      next_narrative: narrative || 'all',
    })
    void trackBusinessEvent('narrative_change', {
      from_narrative: selectedNarrative,
      to_narrative: narrative || 'all',
      date: selectedDate,
    })
    setSelectedNarrative(narrative)
    setPage(1)
  }

  const handleTokenChange = (token?: string) => {
    const normalized = token?.toUpperCase()
    if (normalized === selectedToken) return
    void trackBusinessEvent('token_filter_change', {
      from_token: selectedToken || 'all',
      to_token: normalized || 'all',
      date: selectedDate,
      narrative: selectedNarrative || 'all',
    })
    setSelectedToken(normalized)
    setPage(1)
  }

  const handleToggleFollow = async (event: EventSummary): Promise<FollowToggleResult> => {
    const chatId = getTelegramUserId()
    const isFollowed = followedEventIds.has(event.event_instance_id)

    if (isFollowed) {
      const response = await apiClient.delete<FollowToggleResult>(
        `/api/user/follow/event?chat_id=${chatId}&event_id=${event.event_id}&event_instance_id=${event.event_instance_id}&date=${selectedDate || ''}&narrative_name=${selectedNarrative || event.narrative}`
      )

      if (response.success) {
        setFollowedEventIds((prev) => {
          const next = new Set(prev)
          next.delete(event.event_instance_id)
          return next
        })
        setFollowCount(response.active_count)
        setMaxFollowCount(response.max_count)
        followCountRef.current = response.active_count
        maxFollowCountRef.current = response.max_count
      }

      return response
    }

    const effectiveFollowCount = followCountRef.current + pendingFollowAddsRef.current
    const currentMaxFollowCount = maxFollowCountRef.current

    if (effectiveFollowCount >= currentMaxFollowCount) {
      return {
        success: false,
        error: t(
          `最多只能关注${currentMaxFollowCount}个事件，请付费解锁更多功能`,
          `You can follow up to ${currentMaxFollowCount} events. Please recharge to unlock more.`
        ),
        active_count: followCountRef.current,
        max_count: currentMaxFollowCount,
      }
    }

    pendingFollowAddsRef.current += 1

    const response = await apiClient.post<FollowToggleResult>('/api/user/follow/event', {
      chat_id: chatId,
      event_id: event.event_id,
      event_instance_id: event.event_instance_id,
      date: selectedDate || 'latest',
      event_title: event.title || '',
      narrative_name: selectedNarrative || event.narrative,
      event_data: event,
      analytics_context: getAnalyticsContext(),
    }).finally(() => {
      pendingFollowAddsRef.current = Math.max(0, pendingFollowAddsRef.current - 1)
    })

    setFollowCount(response.active_count)
    setMaxFollowCount(response.max_count)
    followCountRef.current = response.active_count
    maxFollowCountRef.current = response.max_count

    if (response.success) {
      setFollowedEventIds((prev) => {
        const next = new Set(prev)
        next.add(event.event_instance_id)
        return next
      })
    }

    return response
  }

  const handleToggleLike = async (
    event: EventSummary,
    isCurrentlyLiked: boolean,
    likesCountBefore: number,
  ): Promise<LikeToggleResult> => {
    const chatId = getTelegramUserId()

    if (isCurrentlyLiked) {
      const response = await apiClient.delete<LikeToggleResult>(
        '/api/user/like/event',
        {
          chat_id: chatId,
          event_id: event.event_id,
          event_instance_id: event.event_instance_id,
          date: selectedDate || 'latest',
          event_title: event.title || '',
          narrative_name: selectedNarrative || event.narrative,
          likes_count_before: likesCountBefore,
          analytics_context: getAnalyticsContext(),
        }
      )

      if (response.success) {
        setLikedEventIds((prev) => {
          const next = new Set(prev)
          next.delete(event.event_instance_id)
          return next
        })
      }

      return response
    }

    const response = await apiClient.post<LikeToggleResult>('/api/user/like/event', {
      chat_id: chatId,
      event_id: event.event_id,
      event_instance_id: event.event_instance_id,
      date: selectedDate || 'latest',
      event_title: event.title || '',
      narrative_name: selectedNarrative || event.narrative,
      likes_count_before: likesCountBefore,
      analytics_context: getAnalyticsContext(),
    })

    if (response.success) {
      setLikedEventIds((prev) => {
        const next = new Set(prev)
        next.add(event.event_instance_id)
        return next
      })
    }

    return response
  }

  // 鍔犺浇鐘舵€?
  const isInitialLoading = datesLoading || (eventsLoading && displayedEvents.length === 0)
  const isLoadingMore = eventsLoading && displayedEvents.length > 0

  return (
    <div className="min-h-screen bg-gray-50 pb-20 dark:bg-gray-950">
      {/* Header */}
      <Header
        dates={datesData?.dates || []}
        selectedDate={selectedDate}
        selectedNarrative={selectedNarrativeName}
        onDateChange={handleDateChange}
        isLoading={datesLoading}
      />

      {/* Category Tabs */}
      {!narrativesLoading && narratives.length > 0 && (
        <CategoryTabs
          narratives={narratives}
          selectedNarrative={selectedNarrative}
          onNarrativeChange={handleNarrativeChange}
          isLoading={isInitialLoading}
        />
      )}

      <TokenSearch
        symbols={tokenPricesData?.symbols || Object.keys(tokenPricesData?.prices || {})}
        selectedToken={selectedToken}
        onSelect={handleTokenChange}
      />

      {/* 鍐呭鍖哄煙 */}
      <main className="p-4">
        {/* Loading */}
        {isInitialLoading && (
          <LoadingState message={t('加载事件列表...', 'Loading events...')} />
        )}

        {/* Error */}
        {error && (
          <ErrorState
            message={error.message || t('加载失败', 'Failed to load')}
            onRetry={() => refetch()}
          />
        )}

        {/* Empty */}
        {!isInitialLoading && !error && displayedEvents.length === 0 && (
          <EmptyState
            message={t('暂无事件', 'No events yet')}
            description={selectedToken
              ? t(`当前日期没有与 ${selectedToken} 相关的事件`, `No ${selectedToken}-related events on this date`)
              : selectedNarrative
              ? t('该分类暂无事件，请试试其他分类', 'No events in this category. Try another category.')
              : t('当前日期暂无事件数据', 'No event data for this date.')}
          />
        )}

        {/* 浜嬩欢鍒楄〃 */}
        {!isInitialLoading && !error && displayedEvents.length > 0 && (
          <div className="space-y-3">
            {displayedEvents.map((event, index) => (
              <EventCard
                key={event.event_instance_id}
                event={event}
                tokenPrices={tokenPricesData?.prices}
                date={selectedDate}
                narrative={selectedNarrative}
                isFollowed={followedEventIds.has(event.event_instance_id)}
                isLiked={likedEventIds.has(event.event_instance_id)}
                followCount={followCount}
                maxFollowCount={maxFollowCount}
                onToggleFollow={handleToggleFollow}
                onToggleLike={handleToggleLike}
                onFollowNotice={showFollowNotice}
                onBeforeNavigate={saveEventListPosition}
                onClick={() => handleEventClick(event, index)}
              />
            ))}
          </div>
        )}

        {/* 鍒嗛〉 */}
        {eventsData?.has_more && (
          <div className="mt-6 text-center">
            <button
              onClick={() => {
                trackBusinessEvent('load_more', {
                  date: selectedDate,
                  narrative: selectedNarrative || 'all',
                  current_page: page,
                  next_page: page + 1,
                  loaded_count: displayedEvents.length,
                  next_loaded_count: Math.min(
                    displayedEvents.length + PAGE_SIZE,
                    eventsData?.total || totalEventsRef.current || displayedEvents.length + PAGE_SIZE,
                  ),
                  total: eventsData?.total || totalEventsRef.current || displayedEvents.length,
                })
                setPage((p) => p + 1)
              }}
              disabled={isLoadingMore}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50 transition-colors"
            >
              {t('加载更多', 'Load More')}
            </button>
          </div>
        )}
      </main>
      <AppBottomNav />

      {false && (
        <button
          type="button"
          aria-label={t('回到顶部', 'Back to top')}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-5 right-4 z-30 flex h-10 w-10 items-center justify-center rounded-full bg-gray-900 text-lg font-semibold text-white shadow-lg shadow-gray-900/20 transition-colors hover:bg-gray-800 active:scale-95 dark:bg-white dark:text-gray-950 dark:hover:bg-gray-100"
        >
          鈫?        </button>
      )}

      {followNotice && (
        <div className="fixed left-4 right-4 top-4 z-40 mx-auto max-w-sm rounded-lg bg-gray-900 px-4 py-3 text-center text-sm font-medium text-white shadow-lg shadow-gray-900/20 dark:bg-white dark:text-gray-950">
          {followNotice}
        </div>
      )}
    </div>
  )
}
